import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { useUserProfile } from "../contexts/UserProfileContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

export default function EditBlogPost() {
  const { id } = useParams<{ id: string }>(); // Holt die Blogpost-ID aus der URL
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [existingCoverUrl, setExistingCoverUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Blogpost laden
  useEffect(() => {
    const fetchPost = async () => {
      if (!id) return;

      const { data, error } = await supabase
        .from("blog_posts")
        .select("title, content, cover_image_url")
        .eq("id", id)
        .single();

      if (error) {
        setError("Fehler beim Laden des Beitrags.");
      } else if (data) {
        setTitle(data.title);
        setContent(data.content);
        setExistingCoverUrl(data.cover_image_url || null);
      }

      setLoading(false);
    };

    fetchPost();
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    let coverImageUrl = existingCoverUrl;

    // Falls neues Bild gewählt wird, hochladen
    if (coverImage) {
      const { data, error: uploadError } = await supabase.storage
        .from("blog-covers")
        .upload(`cover-${Date.now()}`, coverImage, { upsert: true });

      if (uploadError) {
        setError("Fehler beim Hochladen des neuen Bildes.");
        return;
      }

      coverImageUrl = data?.path
        ? supabase.storage.from("blog-covers").getPublicUrl(data.path).data
            .publicUrl || null
        : null;
    }

    const { error: updateError } = await supabase
      .from("blog_posts")
      .update({
        title,
        content,
        cover_image_url: coverImageUrl,
      })
      .eq("id", id!);

    if (updateError) {
      setError("Fehler beim Speichern.");
    } else {
      setSuccess("✅ Beitrag aktualisiert!");
      navigate("/dashboard"); // oder zurück ins Dashboard
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center text-slate-500">
        Lade Beitrag...
      </div>
    );
  }

  if (!user || !(profile?.role === "creator" || profile?.role === "admin")) {
    return (
      <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
        <p className="text-center text-red-400">
          Du hast keine Berechtigung, Blogposts zu bearbeiten.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d1117]">
      <div className="max-w-2xl mx-auto pt-6 pb-20 px-4 sm:px-6">
        <h1 className="text-2xl font-bold text-slate-100 mb-6">Blogbeitrag bearbeiten</h1>
        <div className="p-4 bg-[#1e2639] border border-[#2a3347] rounded text-sm mb-4">
          <p className="font-semibold mb-2 text-slate-300">🛠 Markdown Tipps:</p>
          <ul className="list-disc list-inside space-y-1 text-slate-400">
            <li>
              <code className="bg-[#0d1117] text-sky-300 px-1 rounded">**fett**</code> → <strong className="text-slate-300">fett</strong>
            </li>
            <li>
              <code className="bg-[#0d1117] text-sky-300 px-1 rounded">*kursiv*</code> → <em className="text-slate-300">kursiv</em>
            </li>
            <li>
              <code className="bg-[#0d1117] text-sky-300 px-1 rounded">~~durchgestrichen~~</code> → <s className="text-slate-300">durchgestrichen</s>
            </li>
            <li>
              <code className="bg-[#0d1117] text-sky-300 px-1 rounded">`Code`</code> → <code className="bg-[#0d1117] text-sky-300 px-1 rounded">Code</code>
            </li>
            <li>
              <code className="bg-[#0d1117] text-sky-300 px-1 rounded">[Linktext](https://example.com)</code> → Link
            </li>
            <li>Tabellen, Checklisten und vieles mehr wird unterstützt!</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            placeholder="Titel"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded outline-none focus:border-sky-400 placeholder:text-slate-600"
            required
          />
          <div className="flex flex-col md:flex-row gap-6">
            {/* Eingabe */}
            <div className="flex-1">
              <label className="block mb-1 text-sm text-slate-400 font-medium">Inhalt (Markdown)</label>
              <textarea
                placeholder="Schreibe hier deinen Inhalt in Markdown..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full p-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded h-72 resize-none outline-none focus:border-sky-400 placeholder:text-slate-600"
                required
              />
            </div>

            {/* Live-Preview */}
            <div className="flex-1 border border-[#2a3347] rounded p-2 bg-[#161b27] overflow-y-auto h-72">
              <label className="block mb-1 text-sm text-slate-400 font-medium">Vorschau</label>
              <div className="prose prose-lg prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeSanitize]}
                >
                  {content || "*Hier erscheint deine Vorschau...*"}
                </ReactMarkdown>
              </div>
            </div>
          </div>

          <div>
            {existingCoverUrl && (
              <img
                src={existingCoverUrl}
                alt="Cover"
                className="w-full h-40 object-cover rounded mb-3 border border-[#2a3347]"
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCoverImage(e.target.files?.[0] || null)}
              className="w-full text-slate-400"
            />
          </div>
          <button
            type="submit"
            className="bg-sky-500 text-white py-2 px-4 rounded hover:bg-sky-600 transition-colors w-full"
          >
            Änderungen speichern
          </button>
        </form>

        {error && <p className="text-red-400 mt-4">{error}</p>}
        {success && <p className="text-emerald-400 mt-4">{success}</p>}
      </div>
    </div>
  );
}
